"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileFieldLogic = void 0;
const filesize_1 = __importDefault(require("filesize"));
const differenceBy_1 = __importDefault(require("lodash-es/differenceBy"));
const file_1 = require("../../aggregations/file");
const file_field_1 = require("../../clipboard/file-field");
const types_1 = require("@smartsuite/types");
const field_logic_1 = require("../../field-type-registry/field-logic");
const comparisons_1 = require("../../comparisons/comparisons");
const file_fieldtype_1 = require("../../safe-field-value/file-field/file.fieldtype");
const texts_1 = require("../../texts");
const intl_1 = require("../../locale/intl");
const validation_1 = require("../../validation/validation");
const file_2 = require("../../safe-field-value/type-checking/file");
class FileFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor(conf) {
        super(conf);
        this.aggregates = {
            empty: { label: (0, intl_1.$t)(texts_1.aggregatesText.empty), aggregator: field_logic_1.countEmpty },
            "non-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.non_empty),
                aggregator: field_logic_1.countFilled,
            },
            "percent-filled": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_filled),
                aggregator: field_logic_1.percentFilled,
            },
            "percent-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_empty),
                aggregator: field_logic_1.percentEmpty,
            },
            "total-attachments": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.total_number_of_attachments),
                aggregator: (0, file_1.fileAggregate)((files) => String(files.length)),
            },
            "total-attachments-size": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.total_attachment_size),
                aggregator: (0, file_1.fileAggregate)((files) => {
                    const totalSize = files.reduce((acc, file) => { var _a; return acc + ((_a = file === null || file === void 0 ? void 0 : file.metadata.size) !== null && _a !== void 0 ? _a : 0); }, 0);
                    return (0, filesize_1.default)(totalSize);
                }),
            },
        };
        this.name = "filefield";
        this.label = (0, intl_1.$t)(texts_1.fileFieldText.files_and_images);
        this.icon = "attachment";
        this.iconColor = "#3A86FF";
        this.placeholder = (0, intl_1.$t)(texts_1.fileFieldText.find_a_value);
        this.searchSynonyms = [
            (0, intl_1.$t)(texts_1.fileFieldText.upload),
            (0, intl_1.$t)(texts_1.fileFieldText.attachment),
        ];
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is_empty: comparisons_1.isEmpty,
            is_not_empty: comparisons_1.isNotEmpty,
            file_name_contains: comparisons_1.fileNameContains,
            file_type_is: comparisons_1.fileTypeIs,
        };
        this.fieldValueGetter = new file_fieldtype_1.FileFieldValueGetter();
        this.clipboardPasteRules = {
            filefield: file_field_1.clipboardFileToFile,
            unknownfield: file_field_1.clipboardFilesToFileField,
        };
        this.filterChoices = conf.file_types || [];
    }
    get validators() {
        return { required: validation_1.required };
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            saveKeys: types_1.navigationKeys,
            editOnStartTyping: false,
            editorWidth: "100%",
            switchToInputControl: "OnCellSelect",
            enableCopyEventInEditMode: true,
            enablePasteEventInEditMode: true,
            inputControlEditMode: "OnShow",
            cellPresenceEventMode: "OnModalOpen",
            allowAddRecordToGroup: false,
        };
    }
    getViewControlRules(field) {
        return {
            ...super.getViewControlRules(field),
            fieldInGroup: "disable",
        };
    }
    getEmptyValue() {
        return [];
    }
    filterValueToString(filterValue) {
        // for a FileField need to return filterValue itself
        return filterValue;
    }
    valueToString(fieldValue, _field, ...rest) {
        if (!Array.isArray(fieldValue)) {
            return "";
        }
        return fieldValue
            .map(({ metadata }) => (metadata === null || metadata === void 0 ? void 0 : metadata.filename) || "")
            .filter(Boolean)
            .join(", ");
    }
    getContentSize(fieldValue) {
        const previewSize = 26;
        return fieldValue.length * previewSize;
    }
    processFieldValue(record, slug, comparison, field) {
        const fieldValue = super.processFieldValue(record, slug, comparison, field);
        return comparison === "file_type_is"
            ? fieldValue.map((item) => item.file_type)
            : fieldValue.map((item) => item.metadata.filename).join(", ");
    }
    getFilterComponentData(field, comparison) {
        return {
            ...super.getFilterComponentData(field, comparison),
            placeholder: comparison === "file_name_contains"
                ? (0, intl_1.$t)(texts_1.fileFieldText.enter_text_here)
                : (0, intl_1.$t)(texts_1.fileFieldText.select_type),
            choices: this.filterChoices,
        };
    }
    formatActivityRecordChangeValues(previousValue, currentValue, _extraData, field) {
        if (this.isValueEmpty(previousValue, field) ||
            this.isValueEmpty(currentValue, field)) {
            return {
                previousValue: previousValue !== null && previousValue !== void 0 ? previousValue : [],
                currentValue: currentValue !== null && currentValue !== void 0 ? currentValue : [],
            };
        }
        const removedValues = (0, differenceBy_1.default)(previousValue, currentValue, (item) => `${item.handle}_${item.metadata.filename}`);
        const addedValues = (0, differenceBy_1.default)(currentValue, previousValue, (item) => `${item.handle}_${item.metadata.filename}`);
        return { previousValue: removedValues, currentValue: addedValues };
    }
    isFieldExpandableOnRecordModal() {
        return true;
    }
    getGridColumnSettings(field) {
        return {
            ...super.getGridColumnSettings(field),
        };
    }
    canChangeValue(field, fromValue, toValue) {
        return this.isValueEqual(fromValue, toValue, field);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 170,
            minWidth: 45,
        };
    }
    getEditRecordInputMenuActions(_field) {
        return [
            {
                action: "downloadAllFiles",
                icon: "download-cloud",
                text: (0, intl_1.$t)(texts_1.fileFieldText.download_all_files),
            },
        ];
    }
    isValueEqual(value1, value2, _field) {
        if ((value1 === null || value1 === void 0 ? void 0 : value1.length) !== (value2 === null || value2 === void 0 ? void 0 : value2.length)) {
            return false;
        }
        value1 = value1 !== null && value1 !== void 0 ? value1 : [];
        value2 = value2 !== null && value2 !== void 0 ? value2 : [];
        return value1.every((item, index) => {
            var _a, _b, _c, _d;
            return item.handle === ((_a = value2 === null || value2 === void 0 ? void 0 : value2[index]) === null || _a === void 0 ? void 0 : _a.handle) &&
                ((_b = item.metadata) === null || _b === void 0 ? void 0 : _b.filename) === ((_d = (_c = value2[index]) === null || _c === void 0 ? void 0 : _c.metadata) === null || _d === void 0 ? void 0 : _d.filename);
        });
    }
    isSupportedByBulkAction() {
        return false;
    }
    getValueFromClipboard(clipboard, targetField, userPreferences, currentValue, extra) {
        const clipboardText = clipboard.getData("text");
        let clipboardJson = { field: {}, value: undefined };
        try {
            clipboardJson = JSON.parse(clipboard.getData("application/json"));
        }
        catch (e) { }
        const clipboardValue = {
            text: clipboardText,
            rawValue: clipboardJson.value,
            files: Array.from(clipboard.files),
        };
        return this.parseClipboardFieldValue(clipboardValue, clipboardJson.field, targetField, userPreferences, currentValue, extra);
    }
    isFullValueLoadNeeded(value) {
        return !(0, file_2.isFullFileFieldValue)(value);
    }
}
exports.FileFieldLogic = FileFieldLogic;
